import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress, 
  Box, 
  Alert,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import ComplianceGraph from './ComplianceGraph';
import SafetyLetterComplianceGraph from './SafetyLetterComplianceGraph';
import ExpenseReportComplianceGraph from './ExpenseReportComplianceGraph';
import LateSubmissionsList from './LateSubmissionsList';
import SupplyOrderReport from './admin/SupplyOrderReport';
import ChecklistStatusSection from './admin/ChecklistStatusSection';
import ExpenseReportStatusSection from './admin/ExpenseReportStatusSection';
import SafetyLetterStatusSection from './admin/SafetyLetterStatusSection';
import { endOfMonth, startOfMonth, addDays, isWithinInterval } from 'date-fns';
import PersonIcon from '@mui/icons-material/Person';

// Create TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`compliance-tabpanel-${index}`}
      aria-labelledby={`compliance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    complianceData: null,
    safetyLetterComplianceData: null,
    expenseReportComplianceData: null,
    lateSubmissions: []
  });

  const { user } = useAuth();
  
  // Toggle to user view
  const switchToUserView = () => {
    localStorage.setItem('adminUserViewMode', 'true');
    window.dispatchEvent(new Event('storage'));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.is_admin) {
        setError('You do not have admin privileges to view this page.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all dashboard data with individual error handling
        let dashboardResponse = null;
        let safetyLettersResponse = [];
        let expenseReportsResponse = null;
        
        try {
          dashboardResponse = await apiService.getAdminDashboardData();
        } catch (dashboardErr) {
          console.error('Error fetching dashboard data:', dashboardErr);
          dashboardResponse = { 
            complianceData: null, 
            lateSubmissions: [] 
          };
        }
        
        try {
          safetyLettersResponse = await apiService.getAllSafetyLetters();
        } catch (safetyErr) {
          console.error('Error fetching safety letters:', safetyErr);
          safetyLettersResponse = [];
        }
        
        try {
          expenseReportsResponse = await apiService.getExpenseReportCompliance();
        } catch (expenseErr) {
          console.error('Error fetching expense report compliance:', expenseErr);
          expenseReportsResponse = null;
        }

        // Calculate safety letter compliance
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();
        const monthEnd = endOfMonth(now);
        const approachingDeadlineStart = addDays(monthEnd, -15); // 15 days before end of month
        
        // Find safety letter for the current month
        const currentLetters = safetyLettersResponse.filter(
          letter => letter.month === currentMonth && letter.year === currentYear
        );

        let safetyLetterCompliance = {
          total_users: dashboardResponse?.complianceData?.total_users || 0,
          on_time: 0,
          approaching: 0,
          overdue: 0
        };

        if (currentLetters.length > 0) {
          const letter = currentLetters[0];
          const signedCount = letter.signature_count || 0;
          const unsignedCount = safetyLetterCompliance.total_users - signedCount;
          
          if (now > monthEnd) {
            // Past the deadline, all unsigned users are overdue
            safetyLetterCompliance.on_time = signedCount;
            safetyLetterCompliance.overdue = unsignedCount;
            safetyLetterCompliance.approaching = 0;
          } else if (isWithinInterval(now, { start: approachingDeadlineStart, end: monthEnd })) {
            // Within 15 days of deadline
            safetyLetterCompliance.on_time = signedCount;
            safetyLetterCompliance.approaching = unsignedCount;
            safetyLetterCompliance.overdue = 0;
          } else {
            // More than 15 days from deadline
            safetyLetterCompliance.on_time = signedCount;
            safetyLetterCompliance.approaching = 0;
            safetyLetterCompliance.overdue = 0;
          }
        }

        setDashboardData({
          complianceData: dashboardResponse?.complianceData || null,
          safetyLetterComplianceData: safetyLetterCompliance,
          expenseReportComplianceData: expenseReportsResponse || null,
          lateSubmissions: dashboardResponse?.lateSubmissions || []
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch dashboard data. Some sections may not display properly.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Set up periodic refresh for expense report data every 15 seconds
    const refreshExpenseData = async () => {
      if (!user?.is_admin) return;
      
      try {
        // Add timestamp to ensure no caching occurs
        const expenseReportsResponse = await apiService.getExpenseReportCompliance();
        
        if (expenseReportsResponse) {
          setDashboardData(prevData => ({
            ...prevData,
            expenseReportComplianceData: expenseReportsResponse
          }));
          
          console.log("Dashboard expense data refreshed:", new Date().toLocaleTimeString());
        }
      } catch (err) {
        console.error('Error refreshing expense report data:', err);
      }
    };
    
    const refreshInterval = setInterval(refreshExpenseData, 15000);
    
    // Clean up interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboardData.complianceData && !dashboardData.lateSubmissions.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={switchToUserView}
          startIcon={<PersonIcon />}
        >
          Switch to User View
        </Button>
      </Box>
      
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Compliance Status Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Compliance Status</Typography>
              <Tabs 
                value={activeTab} 
                onChange={(e, v) => setActiveTab(v)} 
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Vehicle Inspections" />
                <Tab label="Safety Letters" />
                <Tab label="Expense Reports" />
              </Tabs>

              <TabPanel value={activeTab} index={0}>
                <ComplianceGraph data={dashboardData.complianceData} />
                <ChecklistStatusSection />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <SafetyLetterComplianceGraph data={dashboardData.safetyLetterComplianceData} />
                <SafetyLetterStatusSection />
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <ExpenseReportComplianceGraph data={dashboardData.expenseReportComplianceData} />
                <ExpenseReportStatusSection />
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>

        {/* Supply Order Report Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Supply Order Report</Typography>
              <SupplyOrderReport />
            </CardContent>
          </Card>
        </Grid>

        {/* Late Submissions Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Late Submissions</Typography>
              <LateSubmissionsList lateSubmissions={dashboardData.lateSubmissions} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;